import type { Doc, Id } from '@/convex/_generated/dataModel';
import type { ComponentType } from 'react';

export type SearchScope =
  | 'products'
  | 'threads'
  | 'projects'
  | 'avatars'
  | 'aesthetics'
  | 'members'
  | 'scheduled'
  | LinkScope;

/** Scopes whose results are plain links (title, hint, href), sharing one
 * row component and one selection handler. */
export type LinkScope =
  | 'files'
  | 'connectors'
  | 'brands'
  | 'drives'
  | 'shared'
  | 'references'
  | 'channels'
  | 'downloads';

export const LINK_SCOPES: readonly LinkScope[] = [
  'files',
  'connectors',
  'brands',
  'drives',
  'shared',
  'references',
  'channels',
  'downloads',
];

export function isLinkScope(scope: string): scope is LinkScope {
  return (LINK_SCOPES as readonly string[]).includes(scope);
}

export type LinkResult = {
  id: string;
  title: string;
  href: string;
  /** Short trailing text (an account, a count, a percentage). */
  hint?: string;
  /** Shown as relative time when there is no hint. */
  at?: number;
};

export type CommandPickerItem = {
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  hint?: string;
  onSelect: () => void | Promise<void>;
};

export type CommandOpenTab = {
  id: string;
  title: string;
  path: string;
  text?: string | null;
  textLoading?: boolean;
  isActive: boolean;
  activate: () => void;
};

export type CommandSubPage =
  | {
      type: 'project-picker';
      placeholder?: string;
      /** When set, the picker offers a "remove from project" row picking null. */
      noneLabel?: string;
      onPick: (folderId: Id<'threadFolders'> | null) => void | Promise<void>;
    }
  | {
      type: 'product-folder-picker';
      placeholder?: string;
      noneLabel?: string;
      onPick: (folderId: Id<'productFolders'> | null) => void | Promise<void>;
    }
  | { type: 'static'; placeholder?: string; items: CommandPickerItem[] };

type CommandDefBase = {
  /** Stable id, e.g. 'thread.rename'. */
  id: string;
  /** Pre-translated label — registrants own their i18n namespace. */
  label: string;
  icon?: ComponentType<{ className?: string }>;
  keywords?: string[];
  /** Pre-translated section heading; defaults to the page-actions heading. */
  group?: string;
  /** Links into the shortcut registry for Kbd hints. */
  shortcutId?: string;
  destructive?: boolean;
  badge?: number;
};

export type CommandDef = CommandDefBase &
  (
    | {
        /** Executes the command immediately. */
        run: () => void | Promise<void>;
        subPage?: never;
      }
    | {
        /** Opens a picker page for the command. */
        subPage: CommandSubPage;
        run?: never;
      }
  );

export type MenuPage =
  | { kind: 'root' }
  | { kind: 'scope'; scope: SearchScope }
  | { kind: 'actions' }
  | {
      kind: 'picker';
      commandId: string;
      label: string;
      subPage: CommandSubPage;
    };

export const ROOT_PAGE: MenuPage = { kind: 'root' };

export function pageKey(page: MenuPage): string {
  switch (page.kind) {
    case 'root':
      return 'root';
    case 'scope':
      return `scope:${page.scope}`;
    case 'actions':
      return 'actions';
    case 'picker':
      return `picker:${page.commandId}`;
  }
}

/** Prefix sigils kept from the original menu: # products, @ threads, > commands. */
export function pageForPrefix(prefix: string): MenuPage | null {
  if (prefix === '#') return { kind: 'scope', scope: 'products' };
  if (prefix === '@') return { kind: 'scope', scope: 'threads' };
  if (prefix === '>') return { kind: 'actions' };
  return null;
}

export type ProductResult = {
  _id: Id<'products'>;
  _creationTime: number;
  name: string;
  category?: Doc<'products'>['category'];
  createdAt?: number;
  updatedAt?: number;
  organizationId?: Id<'organizations'>;
};

export type OrgThreadResult = {
  _id: Id<'orgThreads'>;
  _creationTime: number;
  routeRef: string;
  title?: string;
  threadId: string;
  organizationId: Id<'organizations'>;
  updatedAt?: number;
  visibility?: Doc<'orgThreads'>['visibility'];
  createdBy?: Doc<'orgThreads'>['createdBy'];
};

export type OrgResult = {
  _id: Id<'organizations'>;
  name: string;
  slug: string;
  role: 'owner' | 'admin' | 'member';
};

export type ProjectResult = {
  _id: Id<'threadFolders'>;
  name: string;
  slug?: string;
  updatedAt: number;
  workspacePath?: string;
};

export type AvatarResult = {
  _id: string;
  name: string;
  thumbnailUrl?: string;
  vfsFolderPath: string;
  scope: 'private' | 'workspace' | 'shared' | 'published';
  _creationTime: number;
};

export type AestheticResult = {
  _id: Id<'aesthetics'>;
  name: string;
  usecase?: 'design' | 'marketing';
  coverImageUrl?: string;
  scope: 'workspace' | 'shared' | 'global';
  _creationTime: number;
};

export type ScheduleResult = {
  _id: Id<'agentSchedules'>;
  title: string;
  /** Next run for active schedules, otherwise the status. */
  hint: string;
};

export type MemberResult = {
  userId: Id<'users'>;
  name?: string;
  email?: string;
  image?: string;
  role: 'owner' | 'admin' | 'member';
};

/** Static "Go to" navigation item, filterable by typed query. */
export type GoToItem = {
  id: string;
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  shortcutId?: string;
  keywords?: string[];
  /** Shown in the default (no query) view; the rest surface on search. */
  pinned?: boolean;
};
